import React from 'react'
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import { KPICardWidgetData } from '../../../types/gotypes'

const ACCENT_COLORS: Record<string, { main: string; light: string }> = {
  blue: { main: '#1976d2', light: '#e3f2fd' },
  green: { main: '#2e7d32', light: '#e8f5e9' },
  red: { main: '#d32f2f', light: '#ffebee' },
  orange: { main: '#ed6c02', light: '#fff3e0' },
  purple: { main: '#7b1fa2', light: '#f3e5f5' },
}

const TREND_COLORS: Record<string, string> = {
  up: '#2e7d32',
  down: '#d32f2f',
  flat: '#757575',
}

const TrendIcon: React.FC<{ trend: string }> = ({ trend }) => {
  const sx = { fontSize: 20, color: TREND_COLORS[trend] || '#757575' }
  switch (trend) {
    case 'up': return <TrendingUpIcon sx={sx} />
    case 'down': return <TrendingDownIcon sx={sx} />
    case 'flat': return <TrendingFlatIcon sx={sx} />
    default: return null
  }
}

interface KPICardWidgetRendererProps {
  data: KPICardWidgetData
}

export const KPICardWidgetRenderer: React.FC<KPICardWidgetRendererProps> = ({ data }) => {
  const accent = ACCENT_COLORS[data.accent || 'blue'] || ACCENT_COLORS.blue

  return (
    <Box
      sx={{
        textAlign: 'center',
        p: 3,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        borderTop: `4px solid ${accent.main}`,
        backgroundColor: '#fff',
      }}
    >
      <Typography
        sx={{
          fontSize: '2.5rem',
          fontWeight: 700,
          color: accent.main,
          lineHeight: 1.1,
        }}
      >
        {data.value}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', mt: 0.5, fontWeight: 500 }}
      >
        {data.label}
      </Typography>
      {data.trend && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1 }}>
          <TrendIcon trend={data.trend} />
          {data.trend_value && (
            <Typography
              variant="body2"
              sx={{ color: TREND_COLORS[data.trend] || '#757575', fontWeight: 600 }}
            >
              {data.trend_value}
            </Typography>
          )}
        </Box>
      )}
      {data.description && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {data.description}
        </Typography>
      )}
    </Box>
  )
}

interface KPICardWidgetEditorProps {
  data: KPICardWidgetData | undefined
  onDataChange: (data: KPICardWidgetData) => void
}

export const KPICardWidgetEditor: React.FC<KPICardWidgetEditorProps> = ({ data, onDataChange }) => {
  const current: KPICardWidgetData = data || { value: '', label: '' }

  const update = (partial: Partial<KPICardWidgetData>) => {
    onDataChange({ ...current, ...partial })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Value"
        size="small"
        fullWidth
        value={current.value}
        onChange={(e) => update({ value: e.target.value })}
        placeholder="e.g. 85%"
      />
      <TextField
        label="Label"
        size="small"
        fullWidth
        value={current.label}
        onChange={(e) => update({ label: e.target.value })}
        placeholder="e.g. Customer Satisfaction"
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Trend</InputLabel>
          <Select
            label="Trend"
            value={current.trend || ''}
            onChange={(e) => update({ trend: e.target.value })}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="up">Up</MenuItem>
            <MenuItem value="down">Down</MenuItem>
            <MenuItem value="flat">Flat</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Trend Value"
          size="small"
          value={current.trend_value || ''}
          onChange={(e) => update({ trend_value: e.target.value })}
          placeholder="e.g. +12%"
          disabled={!current.trend}
        />
      </Box>
      <TextField
        label="Description (optional)"
        size="small"
        fullWidth
        value={current.description || ''}
        onChange={(e) => update({ description: e.target.value })}
        placeholder="e.g. vs. last quarter"
      />

      <Typography variant="overline" color="text.secondary">Accent Color</Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {Object.entries(ACCENT_COLORS).map(([key, colors]) => (
          <Box
            key={key}
            onClick={() => update({ accent: key })}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: colors.main,
              cursor: 'pointer',
              border: '3px solid',
              borderColor: (current.accent || 'blue') === key ? 'grey.800' : 'transparent',
              transition: 'border-color 0.15s',
              '&:hover': { borderColor: (current.accent || 'blue') === key ? 'grey.800' : 'grey.400' },
            }}
          />
        ))}
      </Box>

      {current.value && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>Preview</Typography>
          <KPICardWidgetRenderer data={current} />
        </>
      )}
    </Box>
  )
}
